/* In-page dialog (rendered into <body>): Escape, backdrop click and the × button close it. */
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icons';

export interface ModalAction {
  label: string;
  kind?: 'primary' | 'danger' | 'ghost';
  /** Return false to keep the dialog open. */
  onClick?: () => unknown;
}

export interface ModalProps {
  title: string;
  onClose: () => void;
  actions?: ModalAction[];
  children?: ReactNode;
}

export function Modal({ title, onClose, actions = [], children }: ModalProps) {
  const [shown, setShown] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const closing = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const prevFocus = useRef<HTMLElement | null>(null);

  const close = () => {
    if (closing.current) return;
    closing.current = true;
    setShown(false);
    prevFocus.current?.focus?.();
    setTimeout(() => onCloseRef.current(), 200);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only focus handling
  useEffect(() => {
    prevFocus.current = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => {
      setShown(true);
      box.current?.querySelector<HTMLElement>('button.btn-primary, button.btn, input, select')?.focus();
    });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      prevFocus.current?.focus?.();
    };
  }, []);

  return createPortal(
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape is handled on the document
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click closes the dialog
    <div
      className={`modal-wrap${shown ? ' in' : ''}`}
      onClick={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={title} ref={box}>
        <div className="modal-head">
          <h2>{title}</h2>
          <button type="button" className="icon-btn" aria-label="Kapat" onClick={close}>
            <Icon name="x" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {actions.length ? (
          <div className="modal-actions">
            {actions.map((a) => (
              <button
                key={a.label}
                type="button"
                className={`btn btn-${a.kind ?? 'ghost'}`}
                onClick={async () => {
                  const r = a.onClick ? await a.onClick() : undefined;
                  if (r !== false) close();
                }}
              >
                {a.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
